// ─── MD3 Expressive Menu — Root (Menu, MenuTrigger, MenuContent) ─────────────
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { ScrollArea } from "../scroll-area";
import { MENU_CONTAINER_VARIANTS } from "./menu-animations";
import { MenuProvider, useMenuContext } from "./menu-context";
import {
	BASELINE_COLORS,
	MENU_CONTAINER_SHAPE,
	MENU_GROUP_GAP,
	MENU_MAX_WIDTH,
	MENU_MIN_WIDTH,
	MENU_POPUP_PADDING_Y,
	STANDARD_COLORS,
	VIBRANT_COLORS,
} from "./menu-tokens";
import type {
	MenuContentProps,
	MenuGroupProps,
	MenuItemPosition,
	MenuItemProps,
	MenuProps,
	MenuTriggerProps,
} from "./menu-types";
import { flattenChildren } from "./menu-utils";

// ─── Menu (Root) ──────────────────────────────────────────────────────────────

/**
 * MD3 Expressive Menu root component.
 *
 * Wraps Radix `DropdownMenu.Root` and provides `MenuContext` with `colorVariant`
 * and `open` state to all descendant MenuItem and MenuGroup components.
 *
 * @example
 * <Menu colorVariant="standard">
 *   <MenuTrigger asChild>
 *     <IconButton name="more_vert" />
 *   </MenuTrigger>
 *   <MenuContent>
 *     <MenuGroup index={0} count={2}>
 *       <MenuItem>Cut</MenuItem>
 *       <MenuItem>Copy</MenuItem>
 *     </MenuGroup>
 *     <MenuGroup index={1} count={2}>
 *       <MenuItem>Paste</MenuItem>
 *     </MenuGroup>
 *   </MenuContent>
 * </Menu>
 */
export function Menu({
	children,
	variant,
	menuVariant,
	colorVariant = "standard",
	density = 0,
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
	defaultOpen,
	...props
}: MenuProps &
	Omit<React.ComponentPropsWithoutRef<typeof DropdownMenu.Root>, "children">) {
	// Support deprecated menuVariant prop
	const resolvedVariant = variant ?? menuVariant ?? "baseline";

	// Support both controlled and uncontrolled open state.
	// Initialize internalOpen from defaultOpen so that `defaultOpen={true}` works.
	const [internalOpen, setInternalOpen] = React.useState(
		() => defaultOpen ?? false,
	);
	const isControlled = controlledOpen !== undefined;
	const open = isControlled ? controlledOpen : internalOpen;

	const handleOpenChange = React.useCallback(
		(next: boolean) => {
			if (!isControlled) setInternalOpen(next);
			controlledOnOpenChange?.(next);
		},
		[isControlled, controlledOnOpenChange],
	);

	return (
		<MenuProvider
			variant={resolvedVariant}
			colorVariant={colorVariant}
			density={density}
			open={open}
			onOpenChange={handleOpenChange}
		>
			<DropdownMenu.Root
				{...props}
				defaultOpen={defaultOpen}
				open={isControlled ? open : undefined}
				onOpenChange={handleOpenChange}
			>
				{children}
			</DropdownMenu.Root>
		</MenuProvider>
	);
}
Menu.displayName = "Menu";

// ─── MenuTrigger ──────────────────────────────────────────────────────────────

/**
 * The trigger element that opens/closes the Menu.
 *
 * Use `asChild` to merge trigger behavior with your own element (e.g. a Button or IconButton).
 */
export const MenuTrigger = React.forwardRef<
	React.ComponentRef<typeof DropdownMenu.Trigger>,
	MenuTriggerProps & React.ComponentPropsWithoutRef<typeof DropdownMenu.Trigger>
>(({ children, asChild = true, ...props }, ref) => (
	<DropdownMenu.Trigger ref={ref} asChild={asChild} {...props}>
		{children}
	</DropdownMenu.Trigger>
));
MenuTrigger.displayName = "MenuTrigger";

// ─── MenuContent (popup panel) ────────────────────────────────────────────────

/**
 * The popup container for the menu's contents.
 *
 * Renders into a portal. Uses Radix `forceMount` + Framer Motion `AnimatePresence`
 * so the exit animation (scale + opacity via FastEffects) plays before the portal
 * unmounts. The `open` state is read from `MenuContext`.
 *
 * Transform-origin is automatically set via the Radix CSS variable
 * `--radix-dropdown-menu-content-transform-origin`.
 *
 * @param hasOverflow - Set true when using SubMenu to prevent clipping
 */
export const MenuContent = React.forwardRef<
	React.ComponentRef<typeof DropdownMenu.Content>,
	MenuContentProps &
		Omit<React.ComponentPropsWithoutRef<typeof DropdownMenu.Content>, "asChild">
>(
	(
		{
			children,
			sideOffset = 6,
			side = "bottom",
			align = "start",
			hasOverflow = false,
			colorVariant: propColorVariant,
			separatorStyle = "gap",
			matchTriggerWidth = false,
			maxHeight = "360px",
			viewportRef,
			onViewportRef,
			className,
			...props
		},
		ref,
	) => {
		const {
			open,
			variant,
			colorVariant: contextColorVariant,
		} = useMenuContext();
		const colorVariant = propColorVariant ?? contextColorVariant;

		// Baseline always uses baseline colors; expressive uses colorVariant
		const colors =
			variant === "baseline"
				? BASELINE_COLORS
				: colorVariant === "vibrant"
					? VIBRANT_COLORS
					: STANDARD_COLORS;

		const isExpressiveGap =
			variant === "expressive" && separatorStyle === "gap";

		const setViewportNode = React.useCallback(
			(node: HTMLDivElement | null) => {
				if (typeof viewportRef === "function") {
					viewportRef(node);
				} else if (viewportRef && typeof viewportRef === "object") {
					(
						viewportRef as React.MutableRefObject<HTMLDivElement | null>
					).current = node;
				}
				onViewportRef?.(node);
			},
			[viewportRef, onViewportRef],
		);

		// Memoize containerClassName — cn() is expensive and inputs rarely change
		const containerClassName = React.useMemo(() => {
			const widthClass = matchTriggerWidth
				? "w-[var(--radix-dropdown-menu-trigger-width)]"
				: cn(MENU_MIN_WIDTH, MENU_MAX_WIDTH);

			return variant === "expressive"
				? cn(
						"z-50 flex flex-col",
						widthClass,
						isExpressiveGap ? MENU_GROUP_GAP : "",
						isExpressiveGap ? "bg-transparent" : colors.containerBg,
						isExpressiveGap ? "" : "p-1",
						isExpressiveGap ? "" : "rounded-2xl",
						isExpressiveGap ? "" : "elevation-2",
						hasOverflow || isExpressiveGap
							? "overflow-visible"
							: "overflow-hidden",
						"outline-none",
						className,
					)
				: cn(
						"z-50 flex flex-col",
						widthClass,
						MENU_POPUP_PADDING_Y,
						MENU_GROUP_GAP,
						colors.containerBg,
						MENU_CONTAINER_SHAPE,
						"elevation-2",
						hasOverflow ? "overflow-visible" : "overflow-hidden",
						"outline-none",
						className,
					);
		}, [
			variant,
			isExpressiveGap,
			hasOverflow,
			matchTriggerWidth,
			className,
			colors.containerBg,
		]);

		// Memoize child processing — flattenChildren + cloneElement + optional reduce
		const renderedChildren = React.useMemo(() => {
			if (variant !== "expressive") return children;

			const validChildren = flattenChildren(children);
			const groupCount = validChildren.length;

			const enhanced = validChildren.map((child, i) => {
				if (typeof child.type === "string") return child;

				const defaultItemPosition: MenuItemPosition =
					groupCount === 1
						? "standalone"
						: i === 0
							? "leading"
							: i === groupCount - 1
								? "trailing"
								: "middle";

				const itemPosition =
					(child.props as MenuItemProps).itemPosition ?? defaultItemPosition;

				return React.cloneElement(
					child as React.ReactElement<MenuGroupProps & MenuItemProps>,
					{
						index: i,
						count: groupCount,
						itemPosition,
						isGapVariant: isExpressiveGap,
					},
				);
			});

			if (separatorStyle !== "divider") return enhanced;

			return enhanced.reduce<React.ReactNode[]>((acc, child, i) => {
				if (i > 0) {
					acc.push(
						<hr
							key={`divider-${(child as React.ReactElement).key || i}`}
							className={cn("mx-3 my-0.5 h-px border-0 bg-m3-outline-variant")}
						/>,
					);
				}
				acc.push(child);
				return acc;
			}, []);
		}, [children, variant, isExpressiveGap, separatorStyle]);

		return (
			<AnimatePresence>
				{open && (
					<DropdownMenu.Portal forceMount>
						<DropdownMenu.Content
							ref={ref}
							sideOffset={sideOffset}
							side={side}
							align={align}
							asChild
							forceMount
							{...props}
						>
							<m.div
								role="menu"
								aria-orientation="vertical"
								className={containerClassName}
								variants={MENU_CONTAINER_VARIANTS}
								initial="hidden"
								animate="visible"
								exit="exit"
								style={{
									...(props.style as React.CSSProperties),
									transformOrigin:
										"var(--radix-dropdown-menu-content-transform-origin)",
								}}
							>
								{hasOverflow || !maxHeight ? (
									renderedChildren
								) : (
									<ScrollArea
										style={{ maxHeight }}
										className="w-full flex-1 min-h-0"
										viewportRef={setViewportNode}
									>
										{renderedChildren}
									</ScrollArea>
								)}
							</m.div>
						</DropdownMenu.Content>
					</DropdownMenu.Portal>
				)}
			</AnimatePresence>
		);
	},
);
MenuContent.displayName = "MenuContent";
